// Mirrors askSmart-backend/src/utils/verity/projectStore.ts.

import type { WorkArea } from "../Awp/types";

/**
 * A project's status is DERIVED, never chosen.
 *
 * It used to be a dropdown on the Add Project form, which meant it recorded what somebody last
 * remembered to set rather than where the work actually is. It now falls out of the pipeline — the
 * way a SMRT COP site's status falls out of its progression — so it cannot drift from reality.
 */
export type ProjectStatus = "submitted" | "loaded" | "ready" | "reviewed";

export const STATUS_LABELS: Record<ProjectStatus, string> = {
  submitted: "Submitted",
  loaded: "Loaded",
  ready: "Ready",
  reviewed: "Reviewed",
};

export const STATUS_TONE: Record<ProjectStatus, "pass" | "warning" | "fail" | "info"> = {
  submitted: "info",
  loaded: "info",
  ready: "warning",
  reviewed: "pass",
};

/** Deliverable counts behind the derivation. */
export interface ProjectStats {
  deliverables: number;
  /** Document AI has finished with them. */
  parsed: number;
  /** A person has accepted what Document AI read. */
  reviewed: number;
}

export const EMPTY_STATS: ProjectStats = { deliverables: 0, parsed: 0, reviewed: 0 };

/**
 * Status and the next action, from the state of the work. Kept in step with
 * `deriveProjectProgress` on the server, which is what the list rows are stamped with.
 *
 * `progress` is deliberately an imperative — what to do next, not what happened. A row that says
 * "Parse documents" tells a reviewer where to go; one that says "Partially parsed" does not.
 */
export function deriveProjectProgress(
  stats: ProjectStats,
  complianceRunAt?: string,
): { status: ProjectStatus; progress: string } {
  if (complianceRunAt) return { status: "reviewed", progress: "View findings" };
  if (stats.deliverables === 0) return { status: "submitted", progress: "Add deliverables" };
  if (stats.parsed >= stats.deliverables && stats.reviewed >= stats.deliverables) {
    return { status: "ready", progress: "Run compliance" };
  }
  if (stats.parsed >= stats.deliverables) return { status: "loaded", progress: "Review extraction" };
  return { status: "loaded", progress: "Parse documents" };
}

/** Which engine raised a finding. Deterministic is a rule that failed; AI-assisted is judged. */
export type FindingEngine = "deterministic" | "ai";

export type FindingSeverity = "critical" | "major" | "minor";

/**
 * What a reviewer decided.
 *
 * `open` is the only state the engine can produce — a finding is a claim until a person rules on it.
 * `accepted` means it stands and goes back to the supplier; `dismissed` means it does not, and the
 * reason is kept, because a dismissal without a reason is indistinguishable from an oversight.
 */
export type FindingStatus = "open" | "accepted" | "dismissed";

export const FINDING_STATUS_LABELS: Record<FindingStatus, string> = {
  open: "Open",
  accepted: "Accepted",
  dismissed: "Dismissed",
};

export const SEVERITY_LABELS: Record<FindingSeverity, string> = {
  critical: "Critical",
  major: "Major",
  minor: "Minor",
};

export const ENGINE_LABELS: Record<FindingEngine, string> = {
  deterministic: "Deterministic",
  ai: "AI-Assisted",
};

/**
 * What KIND of claim a finding is.
 *
 *  — the evidence shows the requirement is not met; somebody must ACT.
 *  — the check could not settle it; somebody must LOOK. A different job, and not a
 * defect in the submission. Mixed together, twenty real breaches vanish among six hundred shrugs.
 */
export type FindingKind = "breach" | "unresolved";

/**
 * Where in the submission a finding was read from.
 *
 * Structured rather than a sentence because three things need the pieces: a reviewer opening the
 * sheet needs the page, a re-check comparing cycles needs `absentFrom` to be a list, and an evidence
 * pack needs the quote verbatim. All optional — the engine attaches what it actually knows.
 */
export interface FindingEvidence {
  where: string;
  sheet?: string;
  page?: number;
  region?: "notes" | "title-block" | "labels" | "sweep";
  /** Copied character-for-character from the submission. */
  quote?: string;
  /** Per-sheet requirements: the sheets the engine's own sweep did NOT find it on. */
  absentFrom?: string[];
  sheetsSearched?: number;
}

export interface ComplianceFinding {
  /**
   * Human-facing reference — FND-753482-01.
   *
   * Deterministic, from the rule and the deliverable. That is what lets a re-check after a
   * resubmission line up against the previous cycle instead of raising everything as new — and what
   * lets a reviewer's dismissal survive the supplier sending a corrected drawing.
   */
  id: string;
  title: string;
  detail: string;
  clause: string;
  engine: FindingEngine;
  /** 0–100. A deterministic finding is always 100 — a rule either matched or it did not. */
  confidence: number;
  sourceDeliverableId: string;
  sourceFileName: string;
  /** The discipline of the document it was read from, so findings can be grouped by trade. */
  sourceDiscipline?: string;
  severity: FindingSeverity;
  /** Where it was read from. Absent on runs written before the pointer existed. */
  evidence?: FindingEvidence;
  /** Absent on older runs; those read as breaches. */
  kind?: FindingKind;
  /** The review. Absent on a fresh finding, which reads as `open`. */
  status?: FindingStatus;
  reviewNote?: string;
  reviewedAt?: string;
  reviewedBy?: string;
}

/** What a run could not test, and why. Only meaningful beside the findings. */
export interface RunCoverage {
  rulesConsidered: number;
  rulesChecked: number;
  rulesAwaitingValues: number;
  rulesNeedingPerson: number;
  rulesNotVerifiable: number;
  rulesUnclear: number;
  /**
   * Checked, unsettled, and with nothing to cite — a retrieval miss, held back from the findings
   * list. How well retrieval is working, and the only thing that measures it.
   */
  rulesNoEvidence?: number;
  rulesPassed: number;
  rulesExcluded: number;
  /** Findings a reviewer dismissed in an earlier cycle, brought through untouched and not re-checked. */
  findingsCarriedForward?: number;
  /** Previously-raised findings this cycle POSITIVELY cleared — re-checked and passed. */
  findingsCleared?: number;
  /** Raised last cycle, checked again, still not settled. */
  findingsPersisting?: number;
  deliverablesChecked: number;
  failures: string[];
  /** A person stopped the run before it finished. Its numbers are not a verdict. */
  cancelled?: boolean;
}

/**
 * What one review cycle came to — the count line for a cycle that has been superseded.
 *
 * Summaries, not runs: a full run is 400 KB and a project document has 2 MB to live in. Frozen at
 * the moment the NEXT cycle starts, so "reviewed" records what the reviewer had settled by the time
 * a revision arrived rather than the zero that is true the second a run finishes.
 */
export interface ReviewCycle {
  cycle: number;
  runAt: string;
  runBy?: string;
  deliverableIds: string[];
  counts: ReviewCycleCounts;
}

export interface ReviewCycleCounts {
  breach: number;
  unresolved: number;
  critical: number;
  reviewed: number;
  open: number;
}

export interface ComplianceRun {
  /** When it FINISHED. */
  runAt: string;
  runBy?: string;
  /** The cycles before this one, oldest first. This run is cycle `priorCycles.length + 1`. */
  priorCycles?: ReviewCycle[];
  /** When it started, and how long it took. Absent on runs written before these were stored. */
  startedAt?: string;
  durationMs?: number;
  /** What the run reported as it went — the record of it, once it is no longer progress. */
  steps?: string[];
  deliverableIds: string[];
  standardIds: string[];
  findings: ComplianceFinding[];
  /** Absent on runs written before coverage was recorded. */
  coverage?: RunCoverage;
}

/** Somebody assigned to a project, with the name frozen at the moment they were assigned. */
export interface ProjectReviewer {
  userId: string;
  name: string;
}

/**
 * Who is assigned, whichever shape the record is in.
 *
 * Mirrors the server's own helper. Projects assigned before multiple reviewers existed carry the
 * singular pair, and reading it anywhere else would be a second place that can disagree about who
 * a project belongs to.
 */
export function reviewersOf(project: VerityProject): ProjectReviewer[] {
  if (project.reviewers && project.reviewers.length > 0) return project.reviewers;
  if (project.reviewerId) {
    return [{ userId: project.reviewerId, name: project.reviewerName ?? project.reviewerId }];
  }
  return [];
}

export interface VerityProject {
  id: string;
  orgID: string;
  /**
   * Engineering Review (deliverables) or AWP Review (work packages). Fixed at creation; absent means
   * engineering — every project made before AWP existed is one. Read through reviewTypeOf.
   */
  reviewType?: "engineering" | "awp";
  /** AWP projects: the construction work areas packages are filed under. Set by the administrator. */
  cwas?: WorkArea[];
  name: string;
  /** The client's own project number, shown in the table as "Project ID". */
  projectRef: string;
  supplier: string;
  location?: string;
  discipline?: string;
  notes?: string;
  /**
   * Who is to review this project — one or several.
   *
   * The name is stored beside each id because a project outlives somebody's membership. The
   * singular pair below is the shape this replaced: readable, never written, and only ever read
   * through reviewersOf.
   */
  reviewers?: ProjectReviewer[];
  reviewerId?: string;
  reviewerName?: string;
  /** Set by the compliance engine on its first run. */
  complianceRunAt?: string;
  /** The most recent run and the findings it raised. */
  complianceRun?: ComplianceRun;
  /**
   * An AWP project's most recent cross-package review. Typed loosely here on purpose — this is the
   * engineering side's types file and AWP's shapes live in Awp/types.ts, which must not become a
   * dependency of it. AwpWorkspace narrows it.
   */
  awpRun?: import("../Awp/types").AwpRun;
  awpRunAt?: string;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
  _etag?: string;
  // Stamped onto each row by GET /projects — derived server-side, not stored.
  stats?: ProjectStats;
  status?: ProjectStatus;
  progress?: string;
  /** AWP projects: how many work packages it holds. Stamped by GET /projects. */
  packageCount?: number;
}

export function reviewTypeOf(project: Pick<VerityProject, "reviewType">): "engineering" | "awp" {
  return project.reviewType === "awp" ? "awp" : "engineering";
}

/** What the Add Project form hands back. The server mints id, createdAt and orgID. */
export interface ProjectDraft {
  /** Read by the server on create only — a project never changes type. */
  reviewType?: "engineering" | "awp";
  /** AWP only — the whole work-area list. Rows keep their ids so packages stay filed under them. */
  cwas?: WorkArea[];
  /** AWP only — the version the edit form was opened on; a stale one is refused, not applied. */
  _etag?: string;
  name: string;
  projectRef: string;
  supplier: string;
  location?: string;
  discipline?: string;
  notes?: string;
  /**
   * Who is to do the review. The name travels with each id — see VerityProject.
   *
   * Always sent, empty included: an empty list is how an assignment is cleared, and a field the
   * form only sends when it has a value can never take one away.
   */
  reviewers: ProjectReviewer[];
}

/** Somebody in the org who could be assigned a project. */
export interface Reviewer {
  userId: string;
  name: string;
  email: string;
}

/** One section Document AI found, and how it fared. */
export interface ExtractedSection {
  name: string;
  fields: number;
  status: "pass" | "warning" | "fail";
}

/** One value pulled out of the document, with where it came from and how sure the reader is. */
export interface ExtractedEntity {
  /** The value as written in the document — "OPSD 802.010". */
  value: string;
  /** What kind of value it is — "Referenced Standard", "Governing Standard". */
  type: string;
  source: string;
  /**
   * The PDF pages this value appears on, 1-based. What the viewer jumps to.
   *
   * Absent on deliverables parsed before the viewer existed — those entities do not jump, rather
   * than jumping to page 1 and marking nothing. Re-parse to get them.
   */
  pages?: number[];
  /** 0–100. */
  confidence: number;
}

export interface DeliverableExtraction {
  sections: ExtractedSection[];
  entities: ExtractedEntity[];
  parsedAt: string;
}

/**
 * Whether a submitted pack can be checked against text at all.
 *
 * A scanned drawing set has sheets, has pages, and has a header on each one — so nothing about it
 * looks broken. It simply has no words in it, and a compliance run against it returns almost
 * nothing and calls itself finished. Mirrors assessReadability on the server.
 */
export interface PackReadability {
  verdict: "ok" | "thin" | "unreadable";
  charsPerSheet: number;
  readableSheets: number;
  issues: { code: string; detail: string }[];
}

export interface ProjectDeliverable {
  id: string;
  orgID: string;
  projectId: string;
  fileName: string;
  blobName: string;
  fileSize: number;
  contentType: string;
  docType: string;
  /** Filled in by Document AI; absent until something has actually read the document. */
  discipline?: string;
  ocrQuality?: "good" | "fair" | "poor";
  analysis?: "pending" | "running" | "complete" | "failed";
  /** What Document AI read out of the file. Absent until the deliverable has been parsed. */
  extraction?: DeliverableExtraction;
  /** Counts from the reading, including whether the pack can be checked against text at all. */
  drawingSummary?: { readability?: PackReadability };
  /** Why a parse failed, in the words a reviewer needs. Absent unless `analysis` is "failed". */
  analysisError?: string;
  parsedAt?: string;
  /** Set when a person accepts what Document AI read. */
  humanReviewedAt?: string;
  humanReviewedBy?: string;
  uploadedAt: string;
  uploadedBy?: string;
}

/**
 * One thing that happened to a project.
 *
 * The sentence is written by the server when it happens, not composed here from ids — "Assigned
 * Maurice Byrne" stays true after he has left the organisation. See auditStore.ts.
 */
export interface AuditEvent {
  id: string;
  at: string;
  action: string;
  actorId?: string;
  actorName?: string;
  summary: string;
  detail?: string;
}
